import React from 'react';
import {
  ArrowTopRightOnSquareIcon,
  ArrowsPointingOutIcon,
  ChevronDownIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import DynamicVisualizationPanel from './DynamicVisualizationPanel';
import { SEMHR_BOOKMARKS, SEMHR_TOUR } from './semanticHrTourData';
import { PowerBILogo } from './semanticHrTourCharts';

import { SEMANTIC_HR_TOUR_REF } from '../../../../content/staticAssets';

export const PBI_TOUR_REFS = {
  termination: SEMANTIC_HR_TOUR_REF.termination,
  headcount: SEMANTIC_HR_TOUR_REF.headcount,
  hires: SEMANTIC_HR_TOUR_REF.hires,
};

export function resolvePbiDashboardView(currentStep) {
  switch (currentStep) {
    case 'dash-hires-loading':
      return 'hires-loading';
    case 'dash-hires':
    case 'dynamic-empty':
    case 'dynamic-prompt':
    case 'heatmap':
    case 'line-edit':
    case 'line-chart':
      return 'hires';
    case 'bookmark-open-hires':
    case 'bookmark-pick-hires':
    case 'dash-headcount':
      return 'headcount';
    case 'dash-headcount-loading':
      return 'headcount-loading';
    case 'dash-termination':
    case 'bookmark-open-headcount':
    case 'bookmark-pick-headcount':
      return 'termination';
    default:
      return null;
  }
}

export function resolvePbiBookmarkState(currentStep) {
  switch (currentStep) {
    case 'bookmark-open-headcount':
      return { open: true, highlightId: null };
    case 'bookmark-pick-headcount':
      return { open: true, highlightId: 'headcount' };
    case 'bookmark-open-hires':
      return { open: true, highlightId: null };
    case 'bookmark-pick-hires':
      return { open: true, highlightId: 'hires' };
    default:
      return { open: false, highlightId: null };
  }
}

function PbiFitHost({ children, className = '' }) {
  return (
    <div className={`ia-semhr-pbi-fit-host ${className}`.trim()}>
      {children}
    </div>
  );
}

function SegmentedSwitch({ value, options, className = '' }) {
  return (
    <div className={`inline-flex items-center rounded-full border border-ink-200/90 bg-surface-subtle/60 p-0.5 ${className}`}>
      {options.map((opt) => {
        const active = value === opt.id;
        return (
          <span
            key={opt.id}
            className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 font-semibold leading-none transition-colors ${
              active ? 'bg-white text-brand-700 shadow-sm' : 'text-ink-500'
            }`}
          >
            {opt.icon ? <span className="inline-flex shrink-0 items-center justify-center">{opt.icon}</span> : null}
            {opt.label}
          </span>
        );
      })}
    </div>
  );
}

function PbiChromeButton({ label, icon: Icon }) {
  return (
    <span className="inline-flex h-[16px] items-center justify-center gap-1 rounded border border-ink-200/90 px-1.5 text-[7px] font-medium text-ink-600">
      <span className="flex h-full items-center leading-none">{label}</span>
      <Icon className="h-2.5 w-2.5 shrink-0 stroke-[2]" aria-hidden="true" />
    </span>
  );
}

function PbiHeader({ bookmark, subtitle }) {
  return (
    <div className="flex shrink-0 items-center justify-between border-b border-ink-100 px-2 py-1">
      <div className="min-w-0">
        <p className="truncate text-[8px] font-semibold leading-tight text-ink-900">{bookmark}</p>
        <p className="mt-1 truncate text-[7px] leading-snug text-ink-500">{subtitle || 'SuccessFactors HR Analytics'}</p>
      </div>
      <div className="flex shrink-0 items-center gap-1 self-center">
        <PbiChromeButton label="Open in Power BI" icon={ArrowTopRightOnSquareIcon} />
        <PbiChromeButton label="Expand" icon={ArrowsPointingOutIcon} />
      </div>
    </div>
  );
}

function PbiFilterBar({ pageLabel, bookmarkState }) {
  const { open, highlightId } = bookmarkState;

  return (
    <div className="relative z-30 flex shrink-0 flex-wrap items-center gap-1.5 border-b border-ink-100 bg-white px-2 py-1 text-[7px]">
      <SegmentedSwitch
        value="powerbi"
        options={[
          { id: 'powerbi', label: 'Power BI' },
          { id: 'native', label: 'Native App' },
        ]}
        className="text-[7px]"
      />
      <div className="relative">
        <span
          className={`relative inline-flex items-center gap-0.5 rounded border px-1.5 py-0.5 transition-colors duration-200 ${
            open
              ? 'border-brand-400 bg-brand-50 text-brand-800 shadow-sm ring-1 ring-brand-200/80'
              : 'border-ink-200/90 bg-white text-ink-600'
          }`}
        >
          <Squares2X2Icon className="h-2 w-2 shrink-0 text-ink-400" aria-hidden="true" />
          <span className="max-w-[72px] truncate font-medium">{pageLabel}</span>
          <ChevronDownIcon
            className={`h-2 w-2 shrink-0 text-ink-400 transition-transform duration-200 ${open ? 'rotate-180 text-brand-600' : ''}`}
            aria-hidden="true"
          />
        </span>
        {open ? (
          <div className="ia-semhr-bookmark-menu absolute left-0 top-[calc(100%+3px)] z-40 min-w-[156px] overflow-hidden rounded-md border border-ink-200/90 bg-white py-0.5 shadow-lg">
            {SEMHR_BOOKMARKS.map((b) => {
              const picked = highlightId === b.id;
              const hovered = highlightId && !picked;
              return (
                <p
                  key={b.id}
                  className={`flex items-center gap-1 px-2 py-1 text-[7px] font-semibold uppercase tracking-wide transition-colors ${
                    picked
                      ? 'bg-brand-100 text-brand-800 ring-1 ring-inset ring-brand-200'
                      : hovered
                        ? 'text-ink-500'
                        : 'text-ink-600'
                  }`}
                >
                  <Squares2X2Icon className="h-2 w-2 shrink-0 opacity-60" aria-hidden="true" />
                  {b.label}
                </p>
              );
            })}
          </div>
        ) : null}
      </div>
      <span className="rounded bg-brand-50 px-1 py-px text-brand-700">Year ×</span>
      <span className="text-brand-600">Clear filters</span>
    </div>
  );
}

function PbiConnectingCanvas({ message, centered, fullHeight = false }) {
  return (
    <div className={`flex flex-1 flex-col bg-white ${
      fullHeight
        ? 'min-h-0 items-center justify-center px-3 py-6'
        : `min-h-[120px] ${centered ? 'items-center justify-center px-2 py-4' : 'px-2.5 py-2'}`
    }`}>
      {!centered && !fullHeight && <p className="text-[8px] text-ink-500">{message}</p>}
      <div className={`flex flex-col items-center justify-center ${fullHeight || centered ? '' : 'mt-2 flex-1'}`}>
        <div className={`relative flex items-center justify-center ${fullHeight || centered ? 'h-10 w-10' : 'h-8 w-8'}`}>
          <div className="absolute inset-0 animate-pulse rounded-full bg-[#f5c518]/15" aria-hidden="true" />
          <PowerBILogo className={`relative object-contain ${fullHeight || centered ? 'h-7 w-7' : 'h-6 w-6'}`} />
        </div>
      </div>
      {(centered || fullHeight) && <p className="mt-2.5 text-[9px] font-medium text-ink-600">{message}</p>}
    </div>
  );
}

function PbiLoadingReport({ message, centered, fillPane = false }) {
  return (
    <div className={`flex w-full max-w-full flex-col overflow-hidden bg-white ${fillPane ? 'h-full max-h-full' : 'min-h-full flex-1'}`}>
      <PbiConnectingCanvas message={message} centered={centered} fullHeight={centered} />
    </div>
  );
}

function PbiDashboardBody({ view }) {
  const src = PBI_TOUR_REFS[view];
  if (!src) return null;

  return (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      className="ia-semhr-pbi-media"
      draggable={false}
    />
  );
}

function resolveBookmarkPageLabel(view) {
  if (view === 'headcount-loading' || view === 'headcount') return SEMHR_TOUR.dashboards.headcount.bookmark;
  if (view === 'hires-loading' || view === 'hires') return SEMHR_TOUR.dashboards.hires.bookmark;
  return SEMHR_TOUR.dashboards.termination.bookmark;
}

export default function SemanticHRPbiPanel({
  view,
  bookmarkState,
  scrollRef,
  dynamicPhase = null,
  showDynamicSection = false,
  galleryMode = false,
  className = '',
}) {
  const termDash = SEMHR_TOUR.dashboards.termination;
  const hcDash = SEMHR_TOUR.dashboards.headcount;
  const hiresDash = SEMHR_TOUR.dashboards.hires;

  const isLoading = view === 'headcount-loading' || view === 'hires-loading';
  const headerBookmark =
    view === 'headcount-loading' || view === 'headcount' ? hcDash.bookmark
      : view === 'hires-loading' || view === 'hires' ? hiresDash.bookmark
        : view === 'termination' ? termDash.bookmark
          : 'Dashboard';

  const pageLabel = resolveBookmarkPageLabel(view);

  const loadingConfig = {
    'headcount-loading': { message: 'Loading Power BI report…', centered: true },
    'hires-loading': { message: 'Loading Power BI report…', centered: true },
  };

  const dashboardPane = (
    <div className={`ia-semhr-pbi-pane bg-white ${galleryMode ? 'ia-semhr-pbi-pane--gallery' : 'shrink-0'}`}>
      <PbiFitHost className="ia-semhr-pbi-fit-host--pane bg-white">
        {isLoading ? (
          <PbiLoadingReport {...loadingConfig[view]} centered fillPane />
        ) : (
          <PbiDashboardBody view={view} />
        )}
      </PbiFitHost>
    </div>
  );

  return (
    <div className={`flex min-h-0 flex-1 flex-col overflow-hidden bg-white ${galleryMode ? 'ia-semhr-pbi-panel--gallery' : ''}${showDynamicSection ? ' ia-semhr-pbi-panel--tour' : ''} ${className}`.trim()}
      role="img"
      aria-label="Mock-up of a Power BI workforce dashboard with filters, headcount, hires and termination charts"
    >
      <div aria-hidden="true" className="contents">
      <div className="relative z-20 shrink-0 overflow-visible">
        <PbiHeader bookmark={headerBookmark} />
        <PbiFilterBar pageLabel={pageLabel} bookmarkState={bookmarkState} />
      </div>
      {showDynamicSection ? (
        <div ref={scrollRef} className="ia-semhr-scroll ia-semhr-scroll-decor ia-semhr-right-scroll min-h-0 flex-1 overflow-y-scroll overflow-x-hidden overscroll-contain">
          {dashboardPane}
          <div className="ia-semhr-dv-anchor shrink-0">
            <DynamicVisualizationPanel phase={dynamicPhase ?? 'shell'} stacked />
          </div>
        </div>
      ) : (
        <div className="ia-semhr-right-scroll min-h-0 flex-1 overflow-hidden">
          {dashboardPane}
        </div>
      )}
      </div>
    </div>
  );
}
